import { useResidentList } from '@/hooks/useResidentList';
import { useSelector } from 'react-redux';
import { useParams } from 'react-router-dom';
import { selfDataSelector, showMedicalContentSelector } from '@/redux/config/selfData';
import api from '@/api';
import { fetchCurrentResidents, fetchResidentSortMetadata, createResidentSortData } from '@/api/residents/sortData';
import { IGetNursingHomeCareRecordRes, IResidentCareRecord } from '@/types/api/traversal/getNursingHomeCareRecord';
import { SortType } from '@/types/components';
import { normalizeResidentSortType } from '@/utils/residentSort';

export const useFetchNursingHomeCareRecord = (
  shouldInitFetch = false,
  categories: string[] = [],
  from: string | null = null,
  to: string | null = null,
  sortType: SortType = 'unit',
) => {
  const { nursingHomeId } = useParams<{ nursingHomeId: string }>();
  const { selfUser } = useSelector(selfDataSelector);
  const medical = useSelector(showMedicalContentSelector);
  const permittedUnitId = selfUser.hasUnitPerm ? selfUser.nursingHomeUnitId : null;
  const scope = JSON.stringify([selfUser.id, nursingHomeId, permittedUnitId, medical, from, to]);
  const fetchData = async (): Promise<IGetNursingHomeCareRecordRes> => {
    const [results, residents, current, metadata] = await Promise.all([
      api.get('/traversal/nursing-homes/:nursingHomeId/care-records', {
        params: {
          nursingHomeId: +nursingHomeId,
          ...(permittedUnitId !== null ? { nursingHomeUnitId: permittedUnitId } : {}),
          ...(from ? { from } : {}),
          ...(to ? { to } : {}),
        },
        timeout: 5 * 60 * 1000,
      }),
      api.get('/nursing-homes/:nursingHomeId/residents/active', { params: { nursingHomeId } }),
      fetchCurrentResidents(nursingHomeId, permittedUnitId, medical),
      fetchResidentSortMetadata(nursingHomeId, permittedUnitId),
    ]);
    const activeById = new Map(residents.map((resident) => [resident.id, resident]));
    const metadataById = new Map(metadata.map((resident) => [resident.id, resident]));
    const sortData = createResidentSortData(current, metadata);
    const result: IGetNursingHomeCareRecordRes = {
      nursingHomeId: +nursingHomeId,
      residents: results.residents.map((resident) => {
        const data = sortData.get(resident.residentId);
        const details = metadataById.get(resident.residentId);
        if (!data || !details) throw new Error('Resident sort data is incomplete');
        const active = activeById.get(resident.residentId);
        // 未返却の項目も未設定と扱う。Medicalでは尿デバイスだけを判定する。
        return { ...resident, sortData: data, filterData: {
          nursingHomeUnitId: details.nursingHomeUnitId,
          deviceAssigned: !!active && (active.serialNumber != null || (!medical && active.gasSerialNumber != null)),
          deviceAttached: !!active && (active.urinePercentage != null || (!medical && active.gasPercentage != null)),
        } };
      }),
    };
    return result;
  };

  const { data, residents, status, refresh } = useResidentList<IResidentCareRecord, IGetNursingHomeCareRecordRes>({
    scope,
    enabled: !!selfUser.id,
    autoFetch: shouldInitFetch,
    fetchData,
    categories,
    sortType: normalizeResidentSortType(sortType, medical),
    permittedUnitId,
  });
  const nursingHomeCareRecord = data ? { ...data, residents } : undefined;

  return { nursingHomeCareRecord, fetchNursingHomeCareRecord: refresh, fetchStatus: status };
};
